import { useState, type DragEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  ArrowUp,
  Eye,
  EyeOff,
  GripVertical,
  MoreVertical,
  TableCellsSplit,
  Trash2,
} from 'lucide-react';
import type { ResolvedCustomField } from '@tailoring-crm/shared';
import {
  cn,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@tailoring-crm/ui';

/**
 * A module's field list, in the order its form will meet it, as one compact chip per field.
 *
 * The chip itself is the way to edit: tapping it opens the dialog. Everything else a field can
 * have done to it lives behind its ⋮ menu, and that includes moving it. Moving is offered two
 * ways deliberately: the menu's Move up / Move down is what a 360px phone, a keyboard and a
 * screen reader use, because dragging a row on a phone fights with scrolling the page and loses.
 * Dragging is added on top for a pointer, where it is the faster way to move a field five rows up.
 * Both produce the same request.
 *
 * What a chip tells you at a glance is only what changes how the field behaves: a red asterisk for
 * one of the product's own fields or one the shop made required, a red edge for one that must be
 * filled in, a dot for one the shop added, a muted chip for a hidden one, an icon for one that is
 * not a column.
 *
 * The page renders this once per section — the product's fields, then the shop's — so moving is
 * bounded by the list it is given. `onReorder` hands back only this list's order; the page puts
 * the other section back around it. The rest — its kind, which of OUR fields a renamed one stands in for,
 * its hint and choices — is on hover and in the dialog.
 */

export interface CustomFieldListProps {
  fields: readonly ResolvedCustomField[];
  labelOf: (field: ResolvedCustomField) => string;
  onEdit: (field: ResolvedCustomField) => void;
  onDelete: (field: ResolvedCustomField) => void;
  /** Hide a field that is showing, or show one that is hidden. */
  onToggleHidden: (field: ResolvedCustomField) => void;
  /** The new order of the keys in THIS list. */
  onReorder: (order: string[]) => void;
  disabled?: boolean;
}

/** Required by the shop, or one the product cannot do without (name, phone). */
function mustFill(field: ResolvedCustomField): boolean {
  return field.required || !field.canHide;
}

/** Move one key to a new index, returning the whole order the API expects. */
function moved(keys: readonly string[], from: number, to: number): string[] {
  const next = [...keys];
  const [key] = next.splice(from, 1);
  if (key === undefined) return next;
  next.splice(to, 0, key);
  return next;
}

export function CustomFieldList({
  fields,
  labelOf,
  onEdit,
  onDelete,
  onToggleHidden,
  onReorder,
  disabled = false,
}: CustomFieldListProps): ReactElement {
  const { t } = useTranslation();
  const ns = 'customFields';
  const [dragKey, setDragKey] = useState<string | null>(null);

  const keys = fields.map((field) => field.key);

  const move = (from: number, to: number): void => {
    if (to < 0 || to >= fields.length || from === to) return;
    onReorder(moved(keys, from, to));
  };

  const onDrop = (event: DragEvent<HTMLLIElement>, toIndex: number): void => {
    event.preventDefault();
    if (!dragKey) return;
    const from = keys.indexOf(dragKey);
    setDragKey(null);
    if (from >= 0) move(from, toIndex);
  };

  /** Everything the chip does not show, for the hover tooltip. */
  const detailsOf = (field: ResolvedCustomField): string =>
    [
      field.origin === 'custom' && field.type ? t(`${ns}.type.${field.type}`) : null,
      field.renamed ? t(`${ns}.renamedFrom`, { name: field.defaultLabel }) : null,
      field.origin === 'builtin' ? t('customFields.defaultField') : null,
      field.required || !field.canHide ? t('customFields.mustFill') : null,
      field.hidden ? t(`${ns}.hidden`) : null,
      field.showInTable ? null : t(`${ns}.notInTable`),
      field.helperText,
      field.options.length > 0 ? field.options.join(' · ') : null,
    ]
      .filter(Boolean)
      .join('\n');

  return (
    <ul className="flex flex-col gap-2">
      {fields.map((field, index) => {
        const label = labelOf(field);
        return (
          <li
            key={field.key}
            draggable={!disabled}
            onDragStart={() => setDragKey(field.key)}
            onDragEnd={() => setDragKey(null)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => onDrop(event, index)}
            className={cn(
              'group flex items-center gap-1 rounded-md border border-border bg-card pl-2 text-sm transition-colors hover:bg-accent/50',
              mustFill(field) && 'border-l-2 border-l-destructive',
              dragKey === field.key && 'opacity-50',
              field.hidden && 'bg-muted/40',
            )}
          >
            <GripVertical
              aria-hidden="true"
              className="hidden size-4 shrink-0 cursor-grab text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 md:block"
            />

            <button
              type="button"
              disabled={disabled}
              onClick={() => onEdit(field)}
              aria-label={t(`${ns}.edit`, { name: label })}
              title={detailsOf(field) || undefined}
              className="flex min-w-0 flex-1 items-center gap-1.5 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className={cn('truncate', field.hidden && 'text-muted-foreground')}>
                {label}
              </span>
              {field.origin === 'builtin' || field.required ? (
                <span aria-hidden="true" className="-ml-1 shrink-0 text-destructive">
                  *
                </span>
              ) : null}
              {field.origin === 'custom' ? (
                <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-primary" />
              ) : null}
              {field.hidden ? (
                <EyeOff aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
              ) : null}
              {field.showInTable ? null : (
                <TableCellsSplit
                  aria-hidden="true"
                  className="size-3.5 shrink-0 text-muted-foreground"
                />
              )}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={disabled}
                  aria-label={t('customFields.actions', { name: label })}
                  className="size-9 shrink-0"
                >
                  <MoreVertical aria-hidden="true" className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={index === 0} onSelect={() => move(index, index - 1)}>
                  <ArrowUp aria-hidden="true" />
                  {t(`${ns}.moveUp`, { name: label })}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={index === fields.length - 1}
                  onSelect={() => move(index, index + 1)}
                >
                  <ArrowDown aria-hidden="true" />
                  {t(`${ns}.moveDown`, { name: label })}
                </DropdownMenuItem>
                {/* A field the product reads by name offers no hide, rather than one that fails. */}
                {field.canHide || field.hidden ? (
                  <DropdownMenuItem onSelect={() => onToggleHidden(field)}>
                    {field.hidden ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />}
                    {t(field.hidden ? 'customFields.show' : 'customFields.hide', { name: label })}
                  </DropdownMenuItem>
                ) : null}
                {/* A built-in has no delete: it is hidden, never removed. */}
                {field.origin === 'custom' ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem destructive onSelect={() => onDelete(field)}>
                      <Trash2 aria-hidden="true" />
                      {t(`${ns}.delete`, { name: label })}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
        );
      })}
    </ul>
  );
}
